import CheckCircleIcon from '@offline-mui/icons-material/CheckCircle'
import { Box, Button, Stack, Typography } from '@offline-mui/material'
import { alpha } from '@offline-mui/material/styles'
import type { SabreScheduleOption } from '../api/inventory'
import { formatShortDate } from '../utils/flightFormat'
import {
  FlightLegRow,
  LayoverRow,
  StopsChip,
  durationToMinutes,
  minutesLabel,
  wallClockMinutes,
} from './ui/FlightLegRow'

/** "T1 → T2C", "Departs T1" or "Arrives T2" depending on what Sabre returned. */
function terminalLabel(origin?: string | null, destination?: string | null): string | null {
  if (origin && destination) return `T${origin} → T${destination}`
  if (origin) return `Departs T${origin}`
  if (destination) return `Arrives T${destination}`
  return null
}

type SabreScheduleOptionCardProps = {
  option: SabreScheduleOption
  selected: boolean
  onSelect: () => void
}

export default function SabreScheduleOptionCard({
  option,
  selected,
  onSelect,
}: SabreScheduleOptionCardProps) {
  const legs = option.segments ?? []
  if (legs.length === 0) return null

  const first = legs[0]
  const last = legs[legs.length - 1]
  const stops = Math.max(
    0,
    option.connections != null ? option.connections : legs.length - 1,
  )

  // Leg times are airport-local, so total time is flying time plus layovers
  // (each measured at one airport) rather than last arrival minus first departure.
  const flying = legs.reduce((sum, leg) => sum + (durationToMinutes(leg.duration) ?? 0), 0)
  const waiting = legs.slice(1).reduce((sum, leg, index) => {
    const arrive = wallClockMinutes(legs[index].arrivalDateTime)
    const depart = wallClockMinutes(leg.departureDateTime)
    return arrive != null && depart != null && depart > arrive ? sum + (depart - arrive) : sum
  }, 0)
  const totalLabel =
    minutesLabel(durationToMinutes(option.duration)) ??
    (flying > 0 ? minutesLabel(flying + waiting) : null)

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: selected ? 'primary.main' : 'divider',
        borderRadius: 3,
        bgcolor: 'background.paper',
        boxShadow: (t) =>
          selected ? `0 0 0 3px ${alpha(t.palette.primary.main, 0.14)}` : 'none',
        overflow: 'hidden',
        transition: 'border-color .15s ease, box-shadow .15s ease',
      }}
    >
      <Stack
        direction="row"
        spacing={1.5}
        useFlexGap
        sx={{
          alignItems: 'center',
          flexWrap: 'wrap',
          px: { xs: 2, md: 2.5 },
          py: 1.25,
          bgcolor: (t) => (selected ? alpha(t.palette.primary.main, 0.06) : 'grey.50'),
          borderBottom: '1px solid',
          borderColor: 'divider',
          transition: 'background-color .15s ease',
        }}
      >
        <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
          {first.origin}
          <Box component="span" sx={{ color: 'text.disabled', mx: 0.6, fontWeight: 500 }}>
            →
          </Box>
          {last.destination}
        </Typography>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          {formatShortDate(first.departureDateTime)}
          {totalLabel ? ` · ${totalLabel} total` : ''}
        </Typography>
        <StopsChip stops={stops} />

        <Button
          size="small"
          variant={selected ? 'contained' : 'outlined'}
          onClick={onSelect}
          aria-pressed={selected}
          startIcon={
            selected ? (
              <CheckCircleIcon />
            ) : (
              <Box
                sx={{
                  width: 14,
                  height: 14,
                  borderRadius: '50%',
                  border: '2px solid',
                  borderColor: 'grey.400',
                }}
              />
            )
          }
          sx={{ ml: 'auto', minWidth: 104 }}
        >
          {selected ? 'Selected' : 'Select'}
        </Button>
      </Stack>

      {legs.map((leg, index) => {
        const previous = index > 0 ? legs[index - 1] : null
        const layover = previous
          ? (durationToMinutes(leg.layoverDuration) ??
            (() => {
              const arrive = wallClockMinutes(previous.arrivalDateTime)
              const depart = wallClockMinutes(leg.departureDateTime)
              return arrive != null && depart != null ? depart - arrive : null
            })())
          : null
        const operatedBy =
          leg.operatingCarrier && leg.operatingCarrier !== leg.marketingCarrier
            ? leg.operatingCarrier
            : null

        return (
          <Box key={`${leg.marketingCarrier}-${leg.flightNumber}-${index}`}>
            {previous ? <LayoverRow minutes={layover} airport={leg.origin} /> : null}
            <FlightLegRow
              dense
              carrier={leg.marketingCarrier || '—'}
              flightNumber={leg.flightNumber}
              aircraft={leg.equipmentType}
              origin={leg.origin}
              destination={leg.destination}
              departureDatetime={leg.departureDateTime}
              arrivalDatetime={leg.arrivalDateTime}
              durationMinutes={durationToMinutes(leg.duration)}
              operatedBy={operatedBy}
              trailing={
                terminalLabel(leg.originTerminal, leg.destinationTerminal) ? (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {terminalLabel(leg.originTerminal, leg.destinationTerminal)}
                  </Typography>
                ) : undefined
              }
            />
          </Box>
        )
      })}
    </Box>
  )
}
